/**
 * QA doc processes/tour-search-and-checkout-totals.md R-5, R-NR4 (13.10.2026, Russia -> Oman: every flight was left out because its airports had no
 * district, then every tour was refused for a missing mandatory insurance - both read "Data not found"). A tour search that found no tour says why:
 * online-api sends the refusal code beside the empty list (`noResult`), or as an error answer where CommonSettings:ThrowUserExceptions is on or an
 * older online-api does not know the code. The page shows its own text for the codes below, with the airports the server named in the data part of
 * its text (`<data>{"airports": "MCT, SLL"}</data>`, or `customError` for the transfer refusal); any other code keeps the server's text or "Data not found".
 */
import { TourSearchNoResult } from '@appTypes/api.types';

/** code -> [the text without airports, the text that names them] */
const KEYS: Readonly<Record<string, readonly [string, string?]>> = {
  FLIGHT_AIRPORT_NOT_PLACED: ['TOUR_SEARCH_AIRPORT_NOT_PLACED', 'TOUR_SEARCH_AIRPORT_NOT_PLACED_AIRPORTS'],
  TransferPricesNotFound: ['TOUR_SEARCH_NO_TRANSFER', 'TOUR_SEARCH_NO_TRANSFER_AIRPORTS'],
  InsurancesNotFound: ['TOUR_SEARCH_NO_INSURANCE'],
  FlightPricesNotFound: ['TOUR_SEARCH_NO_FLIGHTS'],
  HotelPricesNotFound: ['TOUR_SEARCH_NO_HOTELS'],
  // packages-api#61: the Destination Module offers this destination to the agent from no flight of the chosen departure city
  DESTINATION_NOT_OFFERED: ['TOUR_SEARCH_DESTINATION_NOT_OFFERED'],
  // flights-api#19: no supplier answered the search (a later search may find tours), and a supplier throttling us for a moment (retry in a minute)
  SUPPLIER_UNAVAILABLE: ['TOUR_SEARCH_SUPPLIER_UNAVAILABLE'],
  SUPPLIER_RATE_LIMITED: ['TOUR_SEARCH_SUPPLIER_RATE_LIMITED'],
  // R-NR7 (packages-api#97): the final transfer prices could not be read - the search stopped (the box shows the search's Trace ID)
  TOUR_TRANSFERS_READ_FAILED: ['TOUR_SEARCH_TRANSFERS_READ_FAILED']
};

/**
 * Server texts of a code that say more than the page's own text and have a line of their own in the locale files, so the page shows them as they are:
 * R-NR5 (packages-api#48) - packages-api TourSearchTransfers.LiveUnavailableMessage, a tour search refused SUPPLIER_UNAVAILABLE because a live transfer
 * supplier did not answer.
 */
const SERVER_TEXTS_KEPT: Readonly<Record<string, readonly string[]>> = {
  SUPPLIER_UNAVAILABLE: [
    'Transfer suppliers did not answer the search, so no tour could be offered. Please search again in a minute.'
  ]
};

/** The airports a server text names in its data part, or null. */
export const refusalAirports = (message: string | null | undefined): string | null => {
  const start = message?.indexOf('<data>') ?? -1;
  const end = message?.lastIndexOf('</data>') ?? -1;
  if (!message || start < 0 || end < start) return null;
  try {
    const data = JSON.parse(message.substring(start + '<data>'.length, end).replace(/'/g, '"')) as Record<
      string,
      unknown
    >;
    const airports = data['airports'] ?? data['customError'];
    return typeof airports === 'string' && airports.trim() ? airports.trim() : null;
  } catch {
    return null;
  }
};

/**
 * R-NR6 (online-api#82, Amiran 22.10.2026): the departure city and the country the server named in the data part of a NO_FLIGHTS_FROM_CITY text, or
 * null when it did not name both.
 */
export const refusalRoute = (message: string | null | undefined): { city: string; country: string } | null => {
  const start = message?.indexOf('<data>') ?? -1;
  const end = message?.lastIndexOf('</data>') ?? -1;
  if (!message || start < 0 || end < start) return null;
  try {
    const data = JSON.parse(message.substring(start + '<data>'.length, end)) as Record<string, unknown>;
    const city = typeof data['city'] === 'string' ? data['city'].trim() : '';
    const country = typeof data['country'] === 'string' ? data['country'].trim() : '';
    return city && country ? { city, country } : null;
  } catch {
    return null;
  }
};

/**
 * The translation key the page shows for a tour-search refusal - with the airports (or, R-NR6, the city and the country) as `<data>` for the site's
 * translate handler, or a kept server text (SERVER_TEXTS_KEPT) - or undefined for a code the page has no text of its own for.
 */
export const tourRefusalMessage = (code: string | null | undefined, message?: string | null): string | undefined => {
  if (code === 'NO_FLIGHTS_FROM_CITY') {
    const route = refusalRoute(message);
    return route ? `TOUR_SEARCH_NO_FLIGHTS_FROM_CITY<data>${JSON.stringify(route)}</data>` : 'TOUR_SEARCH_NO_FLIGHTS';
  }
  const keys = code ? KEYS[code] : undefined;
  if (!code || !keys) return undefined;
  const kept = message?.trim();
  if (kept && SERVER_TEXTS_KEPT[code]?.includes(kept)) return kept;
  const [plain, named] = keys;
  const airports = named ? refusalAirports(message) : null;
  return named && airports ? `${named}<data>${JSON.stringify({ airports })}</data>` : plain;
};

/** The text of an empty tour search: the reason online-api sent, else undefined (the page keeps "Data not found"). */
export const noResultMessage = (noResult: TourSearchNoResult | null | undefined): string | undefined =>
  noResult ? tourRefusalMessage(noResult.code, noResult.message) : undefined;
